import { IconButton } from '@latitude/icon-button';
import { Icon } from '@latitude/icons';
import { cn } from '@latitude/token-utils';
import type { ChangeEvent, ComponentRef, MouseEvent, Ref } from 'react';
import React, { useEffect, useEffectEvent, useId, useState } from 'react';
import type { InputProps } from 'types';

import {
  inputLTElementsVariants,
  inputVariants,
} from './variants/input-variants';
import {
  contentWrapper,
  inputRootVariants,
} from './variants/input-wrapper-variants';

export const Input = ({
  inputSize,
  id,
  value,
  onChange,
  chipsContainer,
  leadingElement,
  trailingElement,
  search = false,
  clearable = true,
  classNames = {},
  onClear,
  error = false,
  rawProps = {},
  ref,
  ...restProps
}: InputProps & {
  ref?: Ref<HTMLInputElement>;
}) => {
  const inputGenId = useId();
  const [inputValue, setInputValue] = useState(value);
  // Render-phase sync, not an effect (LTDS-196).
  const [syncedValue, setSyncedValue] = useState(value);
  if (value !== syncedValue) {
    setSyncedValue(value);
    setInputValue(value);
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    onChange?.(event);
  };

  const handleClear = (event: MouseEvent<HTMLButtonElement>) => {
    onClear?.(event);
    // An onClear that prevents the default owns the text, like the datepicker,
    // whose parent may keep a value the input would never see change.
    if (event.defaultPrevented) {
      return;
    }
    setInputValue('');
    onChange?.({
      target: { value: '' },
      currentTarget: { value: '' },
    } as ChangeEvent<HTMLInputElement>);
  };

  const inputId = id || inputGenId;

  const focusInput = () => {
    document.getElementById(inputId)?.focus();
  };

  const isDisabled = restProps.disabled;

  return (
    <div
      className={cn([
        inputRootVariants({ inputSize, hasError: error }),
        classNames.wrapper,
      ])}
      onClick={focusInput}
    >
      <div
        id="wrapper"
        className="relative flex w-full items-center justify-start"
      >
        {leadingElement && (
          <div
            className={cn(
              inputLTElementsVariants({
                isString: typeof leadingElement === 'string',
                isDisabled,
              }),
              classNames.leadingElement,
            )}
          >
            {leadingElement}
          </div>
        )}
        <div
          id="contentWrapper"
          className={cn([
            contentWrapper({ inputSize }),
            classNames.contentWrapper,
          ])}
        >
          <div className="flex flex-1 flex-wrap gap-1">
            {chipsContainer}
            <input
              ref={ref}
              id={inputId}
              value={inputValue}
              onChange={handleInputChange}
              aria-invalid={error}
              className={cn(
                inputVariants({
                  isDisabled,
                  inputSize,
                }),
                classNames.input,
              )}
              {...restProps}
              {...rawProps}
              readOnly={!!chipsContainer || restProps.readOnly}
              onWheel={(event) => event.currentTarget.blur()} // disables number input scroll
            />
          </div>
        </div>
        {clearable &&
          inputValue &&
          !restProps.disabled &&
          !restProps.readOnly && (
            <IconButton
              size="tiny"
              type="ghost"
              variant="neutral"
              onClick={handleClear}
              data-testid="clear-button"
              htmlType="button"
              aria-label="Clear"
              rawProps={{ tabIndex: -1 }}
              className={cn(
                inputLTElementsVariants({
                  isString: false,
                  isDisabled,
                }),
                classNames.clearButton,
              )}
              icon={<Icon name="x-circle" data-testid="input-clear" />}
            />
          )}
        {search && (
          <IconButton
            size="tiny"
            type="ghost"
            variant="strong"
            htmlType="button"
            aria-label="Search"
            data-testid="search-button"
            disabled={isDisabled}
            // Decorative affordance: the input itself owns focus and search
            // semantics, so keep the button out of the tab order and the
            // a11y tree (mirrors the presentational IconButton pattern).
            rawProps={{ tabIndex: -1, 'aria-hidden': true }}
            className={classNames.trailingElement}
            icon={<Icon name="search" data-testid="input-search" />}
          />
        )}
        {trailingElement && (
          <div
            className={cn(
              inputLTElementsVariants({
                isString: typeof trailingElement === 'string',
                isDisabled,
              }),
              classNames.trailingElement,
            )}
          >
            {trailingElement}
          </div>
        )}
      </div>
    </div>
  );
};

Input.displayName = 'Input';

type DebouncedInputProps = Omit<InputProps, 'onChange'> & {
  onChange?: (value: InputProps['value']) => void;
  debounce?: number;
};

export const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ref,
  ...props
}: DebouncedInputProps & {
  ref?: Ref<ComponentRef<typeof Input>>;
}) => {
  const [value, setValue] = useState(initialValue);
  const [syncedInitialValue, setSyncedInitialValue] = useState(initialValue);
  if (initialValue !== syncedInitialValue) {
    setSyncedInitialValue(initialValue);
    setValue(initialValue);
  }

  // Kept out of the timer's deps: callers pass inline handlers, and re-arming on
  // every new identity re-emits forever when the handler re-renders the caller.
  const emitChange = useEffectEvent((next: InputProps['value']) => {
    onChange?.(next);
  });

  useEffect(() => {
    const timeout = setTimeout(() => {
      emitChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, value]);
  return (
    <Input
      ref={ref}
      value={value}
      onChange={(event: ChangeEvent<HTMLInputElement>) =>
        setValue(event.target.value)
      }
      {...props}
    />
  );
};

DebouncedInput.displayName = 'DebouncedInput';

export { type InputProps };
